<template>
  <section
    class="c_text_icon_cards"
    :data-component-theme="data.componentTheme ?? 'blackBackgroundWhiteText'"
    :class="[`c_text_icon_cards--${cardStyle}`, `c_text_icon_cards--${cardAlignment}`]"
  >
    <div class="c_wrapper_2022">
      <div v-if="data.cards" class="c_text_icon_cards_wrapper" :class="cardGridClass">
        <TextIconCard
          v-for="(card, index) in data.cards"
          :card="card"
          :card-style="data.cardStyle"
          :key="`c_text_icon_card--${index}`"
          :class="{ 'c_text_icon_card--last': index === data.cards.length - 1 && data.cards.length % 2 !== 0 }"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { defineComponent, ref, computed } from '@nuxtjs/composition-api';
import TextIconCard from './TextIconCard.vue';

export default defineComponent({
  name: 'TextIconCards',
  props: {
    data: Object,
  },
  components: {
    TextIconCard,
  },
  setup(props) {
    const cardStyle = ref('card'); // 'card', 'cardLarge', 'borderDivider';
    cardStyle.value = props.data?.cardStyle || 'card';

    const cardAlignment = ref('left'); // 'left', 'center';
    cardAlignment.value = props.data?.alignCenter ? 'center' : 'left';

    // Assign class to handle grid layout
    const cardGridClass = computed(() => {
      const numberOfCards = props.data?.cards?.length ?? 0;
      const sizeKey = numberOfCards >= 5 ? 'many' : numberOfCards;
      return `c_text_icon_cards--${sizeKey}`;
    });

    return { cardStyle, cardAlignment, cardGridClass };
  },
});
</script>

<style lang="scss">
.c_text_icon_cards {
  --text-icon-cards-bg: var(--color-black);
  --text-icon-cards-color: var(--color-white);
  --text-icon-cards-border: rgb(57, 57, 57);
  --text-icon-gap-lg: 60px;
  --text-icon-gap-md: 40px;
  --text-icon-gap: 5vw;

  background-color: var(--text-icon-cards-bg);
  color: var(--text-icon-cards-color);
  padding: px_to_rem(32) 0;

  &[data-component-theme='blackBackgroundWhiteText'] {
    --text-icon-cards-bg: var(--color-black);
    --text-icon-cards-color: var(--color-white);
    --text-icon-cards-border: rgba(113, 113, 113, 1);
  }
  &[data-component-theme='whiteBackgroundBlackText'] {
    --text-icon-cards-bg: var(--color-white);
    --text-icon-cards-color: var(--color-black);
    --text-icon-cards-border: rgb(221, 221, 221);
  }
  &[data-component-theme='darkGrayBackgroundWhiteText'] {
    --text-icon-cards-bg: rgb(39, 39, 39);
    --text-icon-cards-color: var(--color-white);
    --text-icon-cards-border: rgba(113, 113, 113, 1);
  }
  &[data-component-theme='lightGrayBackgroundBlackText'] {
    --text-icon-cards-bg: rgb(221, 221, 221);
    --text-icon-cards-color: var(--color-black);
    --text-icon-cards-border: rgba(178, 178, 178, 1);
  }
  &[data-component-theme='yellowBackgroundBlackText'] {
    --text-icon-cards-bg: var(--color-yellow);
    --text-icon-cards-color: var(--color-black);
    --text-icon-cards-border: rgb(113, 113, 113);
  }

  .c_text_icon_cards_wrapper {
    display: grid;
    gap: var(--text-icon-gap);
    grid-template-columns: 1fr; /* 1 per row */
    grid-auto-flow: row;
  }

  @include mq($mq_sm_to_md) {
    padding: px_to_rem(80) 0;
    .c_text_icon_cards_wrapper {
      gap: var(--text-icon-gap-md);
      grid-auto-flow: row;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      justify-content: center;
    }

    .c_text_icon_cards_wrapper > .c_text_icon_card--last {
      grid-column: 1 / -1;
      justify-self: center;
      width: calc((100% - var(--text-icon-gap-md)) / 2);
      max-width: 100%;
      &.c_text_icon_card--borderDividers {
        width: 100%;
      }
    }
  }

  @include mq($mq_md_to_lg) {
    .c_text_icon_cards_wrapper {
      grid-template-columns: none;
      grid-auto-flow: column;
      grid-auto-columns: minmax(0, 1fr);
      justify-content: stretch;
      align-content: stretch;
    }

    .c_text_icon_cards_wrapper > * {
      min-width: 0;
      // max-width: none;
    }
    .c_text_icon_cards_wrapper > .c_text_icon_card--last {
      grid-column: auto;
      justify-self: center;
      width: 100%;
      // max-width: none;
    }
    .c_text_icon_cards--1 > .c_text_icon_card--cardLarge,
    .c_text_icon_cards--1 > .c_text_icon_card--card,
    .c_text_icon_cards--1 > .c_text_icon_card--borderDividers {
      max-width: 543px;
    }
    .c_text_icon_cards--borderDividers .c_text_icon_cards_wrapper {
      background-color: var(--text-icon-cards-bg);
    }
  }

  &--card {
    --text-icon-gap-lg: 32px;
    --text-icon-gap-md: 40px;
    --text-icon-gap: 5vw;
    .c_text_icon_cards_wrapper {
      gap: var(--text-icon-gap);
      @include mq($mq_sm_to_md) {
        gap: var(--text-icon-gap-md);
      }
      @include mq($mq_md_to_lg) {
        gap: var(--text-icon-gap-lg);
      }
    }
  }
  &--cardLarge {
    --text-icon-gap-lg: 60px;
    --text-icon-gap-md: 40px;
    --text-icon-gap: 5vw;
    .c_text_icon_cards_wrapper {
      gap: var(--text-icon-gap);
      @include mq($mq_sm_to_md) {
        gap: var(--text-icon-gap-md);
      }
      @include mq($mq_md_to_lg) {
        gap: var(--text-icon-gap-lg);
      }
    }
  }
  &--borderDividers {
    --text-icon-gap-lg: 1px;
    --text-icon-gap-md: 1px;
    --text-icon-gap: 1px;

    .c_text_icon_cards_wrapper {
      background-color: var(--text-icon-cards-border);
    }
    .c_text_icon_cards_wrapper.c_text_icon_cards--1 {
      background-color: var(--text-icon-cards-bg);
    }
    .c_text_icon_card__content {
      padding-left: 0;
      padding-right: 0;
    }
    @include mq($mq_sm_to_md) {
      .c_text_icon_card__content {
        padding-left: px_to_rem(32);
        padding-right: px_to_rem(32);
      }
    }
  }
  &--center {
    text-align: center;
    .c_text_icon_card__content {
      align-items: center;
      justify-content: center;
    }
    .c_text_icon_card__cta {
      align-self: center;
    }
    .c_text_icon_card__cta .c_icon_svg {
      margin-right: 0;
    }
  }
}
</style>
